import { useEffect, useState } from 'react';
import * as criminalService from '../../../services/criminalCaseRequests';
import { COURT_TYPE_ID } from '../../../types/criminalApi';
import type { LookupItem } from '../../../mocks/criminalMasterData';

export type CriminalLookupScope = 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'review';

type LookupBundle = {
  provinces: LookupItem[];
  districts: LookupItem[];
  magistrateCourts: LookupItem[];
  highCourts: LookupItem[];
  policeStations: LookupItem[];
  urgencyLevels: LookupItem[];
  suspectStatuses: LookupItem[];
  deliveryMethods: LookupItem[];
  caseSources: LookupItem[];
  genders: LookupItem[];
  corporateInstitutes: LookupItem[];
};

const EMPTY: LookupBundle = {
  provinces: [],
  districts: [],
  magistrateCourts: [],
  highCourts: [],
  policeStations: [],
  urgencyLevels: [],
  suspectStatuses: [],
  deliveryMethods: [],
  caseSources: [],
  genders: [],
  corporateInstitutes: [],
};

let sharedLookups: LookupBundle = { ...EMPTY };

const scopeFetchers: Record<CriminalLookupScope, () => Promise<Partial<LookupBundle>>> = {
  step1: async () => {
    const [prov, src, corp] = await Promise.all([
      criminalService.getProvinces(),
      criminalService.getCaseSources(),
      criminalService.getCorporateInstitutes(),
    ]);
    return {
      provinces: prov.success ? prov.data : [],
      caseSources: src.success ? src.data : [],
      corporateInstitutes: corp.success ? corp.data : [],
    };
  },
  step2: async () => {
    const gen = await criminalService.getGenders();
    return { genders: gen.success ? gen.data : [] };
  },
  step3: async () => {
    const [urg, sus] = await Promise.all([
      criminalService.getUrgencyLevels(),
      criminalService.getSuspectStatuses(),
    ]);
    return {
      urgencyLevels: urg.success ? urg.data : [],
      suspectStatuses: sus.success ? sus.data : [],
    };
  },
  step4: async () => {
    const del = await criminalService.getDeliveryMethods();
    return { deliveryMethods: del.success ? del.data : [] };
  },
  step5: async () => ({}),
  review: async () => {
    const [prov, src, gen, urg, sus, del, corp] = await Promise.all([
      criminalService.getProvinces(),
      criminalService.getCaseSources(),
      criminalService.getGenders(),
      criminalService.getUrgencyLevels(),
      criminalService.getSuspectStatuses(),
      criminalService.getDeliveryMethods(),
      criminalService.getCorporateInstitutes(),
    ]);
    return {
      provinces: prov.success ? prov.data : [],
      caseSources: src.success ? src.data : [],
      genders: gen.success ? gen.data : [],
      urgencyLevels: urg.success ? urg.data : [],
      suspectStatuses: sus.success ? sus.data : [],
      deliveryMethods: del.success ? del.data : [],
      corporateInstitutes: corp.success ? corp.data : [],
    };
  },
};

/**
 * Step-scoped criminal master data. Service-layer cache prevents duplicate network calls.
 */
export function useCriminalLookups(
  scope: CriminalLookupScope,
  provinceId?: string,
  districtId?: string,
) {
  const [data, setData] = useState<LookupBundle>(sharedLookups);
  const [loading, setLoading] = useState(scope !== 'step5');

  useEffect(() => {
    if (scope === 'step5') {
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    void scopeFetchers[scope]().then((partial) => {
      if (!mounted) return;
      sharedLookups = { ...sharedLookups, ...partial };
      setData({ ...sharedLookups });
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [scope]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!provinceId) {
      setData((d) => ({ ...d, districts: [] }));
      return;
    }

    let mounted = true;
    void criminalService.getDistricts(provinceId).then((r) => {
      if (!mounted) return;
      const districts = r.success ? r.data : [];
      sharedLookups = { ...sharedLookups, districts };
      setData((d) => ({ ...d, districts }));
    });
    return () => {
      mounted = false;
    };
  }, [scope, provinceId]);

  useEffect(() => {
    if (scope !== 'step1' && scope !== 'review') return;
    if (!districtId) {
      setData((d) => ({ ...d, magistrateCourts: [], highCourts: [], policeStations: [] }));
      return;
    }

    let mounted = true;
    void Promise.all([
      criminalService.getCourts(COURT_TYPE_ID.MAGISTRATE_COURT, districtId, provinceId),
      criminalService.getCourts(COURT_TYPE_ID.HIGH_COURT, districtId, provinceId),
      criminalService.getPoliceStations(districtId),
    ]).then(([mag, high, police]) => {
      if (!mounted) return;
      const magistrateCourts = mag.success ? mag.data : [];
      const highCourts = high.success ? high.data : [];
      const policeStations = police.success ? police.data : [];
      sharedLookups = { ...sharedLookups, magistrateCourts, highCourts, policeStations };
      setData((d) => ({ ...d, magistrateCourts, highCourts, policeStations }));
    });

    return () => {
      mounted = false;
    };
  }, [scope, districtId, provinceId]);

  return {
    loading,
    provinces: data.provinces,
    districts: data.districts,
    courts: data.magistrateCourts,
    magistrateCourts: data.magistrateCourts,
    highCourts: data.highCourts,
    policeStations: data.policeStations,
    urgencyLevels: data.urgencyLevels,
    suspectStatuses: data.suspectStatuses,
    deliveryMethods: data.deliveryMethods,
    caseSources: data.caseSources,
    genders: data.genders,
    corporateInstitutes: data.corporateInstitutes,
  };
}
